export function normalizeOptimizeJson(raw = {}) {
  const safeNum = (v, d = 0) => (v == null ? d : Number(v));
  const safeStr = (v, d = "") => (v == null ? d : String(v));

  const co = raw?.cost_optimization || {};
  const coSummary = co?.summary || {};
  const costOptimization = {
    enabled: Boolean(co.enabled),
    description: safeStr(co.description),
    // Agent's honest outcome message (e.g. "No cheaper replacement was found...") —
    // shown INSTEAD of the generic description when nothing was actually replaced.
    message: safeStr(co.message),
    summary: {
      originalTotalCost: safeNum(coSummary.original_total_cost),
      optimizedTotalCost: safeNum(coSummary.optimized_total_cost),
      totalSavings: safeNum(coSummary.total_savings),
      savingsPercentage: safeNum(coSummary.savings_percentage),
      tasksAnalyzed: safeNum(coSummary.tasks_analyzed),
      tasksOptimized: safeNum(coSummary.tasks_optimized),
      skillMatchThreshold: safeStr(coSummary.skill_match_threshold),
    },
    replacements: Array.isArray(co.replacements) ? co.replacements : [],
  };

  // workload_balancing: new API uses a flat `overutilized_jobs` array (one entry
  // per job-task pair). Group by job_id to produce the per-job shape the UI expects.
  const wb = raw?.workload_balancing || {};
  const flatOverutilized = Array.isArray(wb.overutilized_jobs) ? wb.overutilized_jobs : [];

  const jobMap = new Map();
  for (const entry of flatOverutilized) {
    const key = entry.job_id;
    if (!jobMap.has(key)) {
      jobMap.set(key, {
        job_name: safeStr(entry.job_name),
        utilization_before: safeNum(entry.utilization_pct),
        utilization_after: safeNum(entry.utilization_pct),
        tasks: [],
      });
    }
    const replaced = entry.replacement?.status === "replaced";
    jobMap.get(key).tasks.push({
      task_name: safeStr(entry.task_name),
      raci_role: safeStr(entry.raci_role),
      status: replaced ? "moved" : "kept",
      redistributed_to: replaced
        ? safeStr(entry.replacement?.replacement_job_name || "") || null
        : null,
    });
  }

  // receiving_jobs: built from entries where a replacement actually happened
  const receivingMap = new Map();
  for (const entry of flatOverutilized) {
    if (entry.replacement?.status === "replaced" && entry.replacement?.replacement_job_name) {
      const name = entry.replacement.replacement_job_name;
      if (!receivingMap.has(name)) {
        receivingMap.set(name, { job_name: name, tasks_received: 0, utilization_after: 0 });
      }
      receivingMap.get(name).tasks_received += 1;
    }
  }

  const workloadBalancing = {
    enabled: flatOverutilized.length > 0,
    overloaded_jobs: Array.from(jobMap.values()),
    receiving_jobs: Array.from(receivingMap.values()),
    // new fields passed through for the UI update
    message: safeStr(wb.message),
    summary: wb.summary || {},
    frozen_jobs: Array.isArray(wb.frozen_jobs) ? wb.frozen_jobs : [],
    threshold: wb.threshold || {},
    // Per-job utilization rows (always present from the agent) so the analyst can see
    // every involved job's measured load even when nothing is overutilized.
    jobs: Array.isArray(wb.jobs) ? wb.jobs : [],
  };

  return {
    ...raw,
    normalized: {
      costOptimization,
      workloadBalancing,
    },
  };
}

const optimizeNormalize = { normalizeOptimizeJson };

export default optimizeNormalize;
